import React, { useState, useEffect, useRef } from 'react';
import { Delete, Add } from '@mui/icons-material';
import { Search, ChevronDown } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';
import BaseModal from '../../../utils/BaseModal';
import { SUPPORTED_COUNTRIES, Country } from '../../../navbar/wallet/countries';
import {
    moneyFromMinor,
    moneyToMinor,
    percentFromScaled,
    percentToScaled,
} from '../../../utils/UnitConversion.utils';

export interface CountryConfigItem {
    countryCode: string;
    inputCurrency: string;
    outputCurrency: string;
    fixedMinor: number;
    feeBps: number;
    fxFeeBps: number;
    minAmountMinor: number;
    maxAmountMinor: number;
    kycRequired: boolean;
    isActive: boolean;
}

interface DisplayCountryConfigItem {
    countryCode: string;
    inputCurrency: string;
    outputCurrency: string;
    fixedAmount: number | string;
    feePercent: number | string;
    fxFeePercent: number | string;
    minAmount: number | string;
    maxAmount: number | string;
    kycRequired: boolean;
    isActive: boolean;
}

interface PaymentMethodEditModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (methodId: string, configs: CountryConfigItem[]) => Promise<void>;
    methodId: string | null;
    methodName: string | null;
    initialConfigs: CountryConfigItem[];
    availableCountryCodes?: string[];
    loading?: boolean;
}

// Converts matrix API response structure to flat country config array for modal consumption
export const mapMatrixToConfigs = (matrixData: any[], methodId: string): CountryConfigItem[] => {
    if (!Array.isArray(matrixData) || !methodId) return [];
    return matrixData
        .filter(item => item && item.providers && item.providers[methodId])
        .map(item => {
            const provider = item.providers[methodId];
            return {
                countryCode: item.countryCode,
                inputCurrency: provider.inputCurrency || 'EUR',
                outputCurrency: provider.outputCurrency || 'EUR',
                fixedMinor: provider.fixedMinor ?? 0,
                feeBps: provider.feeBps ?? 0,
                fxFeeBps: provider.fxFeeBps ?? 0,
                minAmountMinor: provider.minAmountMinor ?? 0,
                maxAmountMinor: provider.maxAmountMinor ?? 0,
                kycRequired: provider.kycRequired ?? false,
                isActive: provider.enabled ?? true,
            };
        });
};

// Formats country ISO code using countries.ts lookup with Intl fallback
const getCountryDisplayName = (code: string): string => {
    if (!code) return '';
    const upperCode = code.toUpperCase();
    const found = (SUPPORTED_COUNTRIES as Country[] | undefined)?.find(c => c.code.toUpperCase() === upperCode);
    if (found && found.name) {
        return `${found.name} (${upperCode})`;
    }
    try {
        const regionNames = new Intl.DisplayNames(['de', 'en'], { type: 'region' });
        const name = regionNames.of(upperCode);
        return name ? `${name} (${upperCode})` : upperCode;
    } catch {
        return upperCode;
    }
};

// Converts backend minor integer value (cents) to major decimal currency value
const toDisplayAmount = (minor: number): number => {
    return moneyFromMinor(minor);
};

// Converts major decimal currency value back to backend minor integer unit (cents)
const toMinorAmount = (amount: number | string): number => {
    return moneyToMinor(Number(amount) || 0);
};

// Converts backend BPS integer value to display percentage rate
const toDisplayPercent = (bps: number): number => {
    return percentFromScaled(bps);
};

// Converts percentage display value back to backend BPS integer unit
const toBpsPercent = (percent: number | string): number => {
    return percentToScaled(Number(percent) || 0);
};

const PaymentMethodEditModal: React.FC<PaymentMethodEditModalProps> = ({
                                                                           open,
                                                                           onClose,
                                                                           onSave,
                                                                           methodId,
                                                                           methodName,
                                                                           initialConfigs,
                                                                           loading = false,
                                                                       }) => {
    const [configs, setConfigs] = useState<DisplayCountryConfigItem[]>([]);
    const [selectedNewCountry, setSelectedNewCountry] = useState<string>('');
    const [dropdownSearch, setDropdownSearch] = useState<string>('');
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            const mappedConfigs: DisplayCountryConfigItem[] = (initialConfigs || []).map(c => ({
                countryCode: c.countryCode.toUpperCase(),
                inputCurrency: c.inputCurrency,
                outputCurrency: c.outputCurrency,
                fixedAmount: toDisplayAmount(c.fixedMinor),
                feePercent: toDisplayPercent(c.feeBps),
                fxFeePercent: toDisplayPercent(c.fxFeeBps),
                minAmount: toDisplayAmount(c.minAmountMinor),
                maxAmount: toDisplayAmount(c.maxAmountMinor),
                kycRequired: c.kycRequired,
                isActive: c.isActive,
            }));
            setConfigs(mappedConfigs);
            setSelectedNewCountry('');
            setDropdownSearch('');
            setIsDropdownOpen(false);
        }
    }, [open, initialConfigs]);

    // Closes custom dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleAddCountry = () => {
        if (!selectedNewCountry) return;
        const normalizedCode = selectedNewCountry.toUpperCase();
        if (configs.some(c => c.countryCode.toUpperCase() === normalizedCode)) return;

        const newConfig: DisplayCountryConfigItem = {
            countryCode: normalizedCode,
            inputCurrency: 'EUR',
            outputCurrency: 'EUR',
            fixedAmount: 0,
            feePercent: 0,
            fxFeePercent: 0,
            minAmount: 0,
            maxAmount: 1000,
            kycRequired: false,
            isActive: true,
        };

        setConfigs([...configs, newConfig]);
        setSelectedNewCountry('');
        setIsDropdownOpen(false);
        setDropdownSearch('');
    };

    const handleRemoveCountry = (countryCode: string) => {
        const normalizedCode = countryCode.toUpperCase();
        setConfigs(configs.filter(c => c.countryCode.toUpperCase() !== normalizedCode));
    };

    const handleFieldChange = <K extends keyof DisplayCountryConfigItem>(
        countryCode: string,
        field: K,
        value: DisplayCountryConfigItem[K]
    ) => {
        const normalizedCode = countryCode.toUpperCase();
        setConfigs(prev =>
            prev.map(item => {
                if (item.countryCode.toUpperCase() === normalizedCode) {
                    return { ...item, [field]: value };
                }
                return item;
            })
        );
    };

    const handleSubmit = async () => {
        if (!methodId) return;

        const convertedConfigs: CountryConfigItem[] = configs.map(c => ({
            countryCode: c.countryCode.toUpperCase(),
            inputCurrency: c.inputCurrency,
            outputCurrency: c.outputCurrency,
            fixedMinor: toMinorAmount(c.fixedAmount),
            feeBps: toBpsPercent(c.feePercent),
            fxFeeBps: toBpsPercent(c.fxFeePercent),
            minAmountMinor: toMinorAmount(c.minAmount),
            maxAmountMinor: toMinorAmount(c.maxAmount),
            kycRequired: c.kycRequired,
            isActive: c.isActive,
        }));

        await onSave(methodId, convertedConfigs);
    };

    // Displays all supported countries filtered only by search term without exclusions
    const allSupportedList = (SUPPORTED_COUNTRIES as Country[] | undefined) || [];
    const dropdownFilteredCountries = allSupportedList.filter(country => {
        const query = dropdownSearch.toLowerCase().trim();
        if (!query) return true;
        const nameMatch = country.name?.toLowerCase().includes(query);
        const codeMatch = country.code?.toLowerCase().includes(query);
        return nameMatch || codeMatch;
    });

    return (
        <BaseModal
            open={open}
            title={`Edit Country Configurations - ${methodName || ''}`}
            onAbort={onClose}
            onSubmit={handleSubmit}
            abortText="Cancel"
            submitText="Save Changes"
            submitColor="primary"
            loading={loading}
            maxWidth="lg"
        >
            <div className="flex flex-col gap-6 py-2">
                <div className="flex items-center gap-3 p-3 bg-neutral-100 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
                    <div className="relative flex-1" ref={dropdownRef}>
                        <button
                            type="button"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="w-full flex items-center justify-between gap-3 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded-md px-3 py-1.5 text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                        >
                            <div className="flex items-center gap-2 truncate">
                                {selectedNewCountry ? (
                                    <>
                                        <ReactCountryFlag svg countryCode={selectedNewCountry} style={{ width: '1.2em', height: '1.2em', flexShrink: 0 }} />
                                        <span className="truncate">{getCountryDisplayName(selectedNewCountry)}</span>
                                    </>
                                ) : (
                                    <span className="text-gray-400">Select a country to add...</span>
                                )}
                            </div>
                            <ChevronDown size={14} className={`text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-md shadow-lg z-50 overflow-hidden flex flex-col">
                                <div className="p-2 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                                    <div className="relative">
                                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search country..."
                                            value={dropdownSearch}
                                            onChange={e => setDropdownSearch(e.target.value)}
                                            className="w-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded pl-7 pr-2 py-1 text-xs text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <div className="max-h-56 overflow-y-auto p-1 flex flex-col gap-0.5">
                                    {dropdownFilteredCountries.length > 0 ? (
                                        dropdownFilteredCountries.map(country => {
                                            const codeUpper = country.code.toUpperCase();
                                            const isAlreadyAdded = configs.some(c => c.countryCode.toUpperCase() === codeUpper);
                                            return (
                                                <div
                                                    key={country.code}
                                                    onClick={() => {
                                                        setSelectedNewCountry(codeUpper);
                                                        setIsDropdownOpen(false);
                                                        setDropdownSearch('');
                                                    }}
                                                    className={`flex items-center justify-between px-3 py-1.5 rounded text-xs cursor-pointer transition-colors ${selectedNewCountry === codeUpper ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-gray-900 dark:text-neutral-200'}`}
                                                >
                                                    <div className="flex items-center gap-2 truncate">
                                                        <ReactCountryFlag svg countryCode={country.code} style={{ width: '1.2em', height: '1.2em', flexShrink: 0 }} />
                                                        <span className="truncate">{country.name} ({codeUpper})</span>
                                                    </div>
                                                    {isAlreadyAdded && (
                                                        <span className="text-[10px] text-gray-400 dark:text-neutral-500 italic shrink-0">Added</span>
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="p-2 text-center text-xs text-neutral-500">No country found</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleAddCountry}
                        disabled={!selectedNewCountry}
                        className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2 rounded-md transition-colors"
                    >
                        <Add fontSize="small" /> Add Country
                    </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto border border-neutral-200 dark:border-neutral-800 rounded-lg">
                    <table className="w-full text-left text-xs">
                        <thead className="bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 font-semibold sticky top-0 uppercase tracking-wider">
                        <tr>
                            <th className="p-3">Country</th>
                            <th className="p-3">Active</th>
                            <th className="p-3">Input / Output Curr.</th>
                            <th className="p-3">Fixed Fee</th>
                            <th className="p-3">Fee (%)</th>
                            <th className="p-3">FX Fee (%)</th>
                            <th className="p-3">Min / Max Amount</th>
                            <th className="p-3">KYC</th>
                            <th className="p-3 text-right">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                        {configs.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="p-4 text-center text-neutral-500">
                                    No country configurations added yet.
                                </td>
                            </tr>
                        ) : (
                            configs.map(cfg => (
                                <tr key={cfg.countryCode} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/30">
                                    <td className="p-3 font-bold text-gray-900 dark:text-white">
                                        <div className="flex items-center gap-2">
                                            <ReactCountryFlag svg countryCode={cfg.countryCode} style={{ width: '1.2em', height: '1.2em', flexShrink: 0 }} />
                                            <span>{getCountryDisplayName(cfg.countryCode)}</span>
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        <input
                                            type="checkbox"
                                            checked={cfg.isActive}
                                            onChange={e => handleFieldChange(cfg.countryCode, 'isActive', e.target.checked)}
                                            className="rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                        />
                                    </td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="text"
                                                value={cfg.inputCurrency}
                                                onChange={e => handleFieldChange(cfg.countryCode, 'inputCurrency', e.target.value)}
                                                className="w-14 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded px-1.5 py-1 text-xs text-gray-900 dark:text-white"
                                            />
                                            <span>/</span>
                                            <input
                                                type="text"
                                                value={cfg.outputCurrency}
                                                onChange={e => handleFieldChange(cfg.countryCode, 'outputCurrency', e.target.value)}
                                                className="w-14 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded px-1.5 py-1 text-xs text-gray-900 dark:text-white"
                                            />
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={cfg.fixedAmount}
                                            onChange={e => handleFieldChange(cfg.countryCode, 'fixedAmount', e.target.value)}
                                            className="w-20 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded px-1.5 py-1 text-xs text-gray-900 dark:text-white"
                                        />
                                    </td>
                                    <td className="p-3">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={cfg.feePercent}
                                            onChange={e => handleFieldChange(cfg.countryCode, 'feePercent', e.target.value)}
                                            className="w-16 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded px-1.5 py-1 text-xs text-gray-900 dark:text-white"
                                        />
                                    </td>
                                    <td className="p-3">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={cfg.fxFeePercent}
                                            onChange={e => handleFieldChange(cfg.countryCode, 'fxFeePercent', e.target.value)}
                                            className="w-16 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded px-1.5 py-1 text-xs text-gray-900 dark:text-white"
                                        />
                                    </td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={cfg.minAmount}
                                                onChange={e => handleFieldChange(cfg.countryCode, 'minAmount', e.target.value)}
                                                className="w-20 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded px-1.5 py-1 text-xs text-gray-900 dark:text-white"
                                            />
                                            <span>-</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={cfg.maxAmount}
                                                onChange={e => handleFieldChange(cfg.countryCode, 'maxAmount', e.target.value)}
                                                className="w-24 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded px-1.5 py-1 text-xs text-gray-900 dark:text-white"
                                            />
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        <input
                                            type="checkbox"
                                            checked={cfg.kycRequired}
                                            onChange={e => handleFieldChange(cfg.countryCode, 'kycRequired', e.target.checked)}
                                            className="rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                        />
                                    </td>
                                    <td className="p-3 text-right">
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveCountry(cfg.countryCode)}
                                            className="text-red-500 hover:text-red-700 p-1 transition-colors"
                                        >
                                            <Delete fontSize="small" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        </BaseModal>
    );
};

export default PaymentMethodEditModal;